/**
 * 伞文化数据 — 中国伞历史
 *
 * 文字信息已于 2026-07-18 经第二轮逐条核查，仅采用真实史料与官方渠道
 * （博物馆官方藏品资料、中国非物质文化遗产网、《考工记》《孔子家语》
 * 《后汉书》《宋史》《明史》《天工开物》《格致镜原》《南村辍耕录》
 * 等传世文献、人民日报/新华社等官方媒体）。
 * 每条 sources 字段为该条目出处，prototypes 为史料原型参考图，
 * modelBasis 为 3D 模型形态依据（内部建模使用）。
 * hidden: true 的条目（唐）暂不在时间轴展示。
 */

// 构建模式：仅小红书小工具打包（vite --mode xhs）时为 true。
// 小红书包体限制下，元/明/民国八角三款大模型以高清静态渲染图替代；
// 完整版（dev / 默认 build）正常加载 3D 模型。
const IS_XHS = import.meta.env.MODE === 'xhs';

export const umbrellaData = [
  {
    id: "pre_qin",
    era: "先秦",
    period: "约公元前11世纪 — 公元前221年",
    name: "华盖",
    nameEn: "Imperial Canopy",
    description: "丝帛为面，张于车驾，是贵族仪仗用器；「伞」字目前已知最早见于睡虎地秦简。",
    materials: ["丝帛盖衣", "竹木盖弓"],
    significance: "伞的雏形，权力与身份象征",
    museumItem: "秦陵一号铜车马伞盖（秦始皇帝陵博物院藏）",
    tabs: {
      overview: "伞在先秦称「盖」或「簦」，为车驾仪仗之器。晋·崔豹《古今注》载「华盖，黄帝所作也」；《考工记》有「轮人为盖」，战国《人物御龙帛画》中已有伞盖形象。",
      craft: "以秦陵一号铜车马伞盖为实证：伞盖高114厘米、直径122厘米，由盖斗与22根盖弓构成，插装于可拆卸的盖座之上。",
      culture: "华盖是礼制与身份的标志。《孔子家语·七十二弟子解》载「孔子将近行，命从者皆持盖」。此时的盖用于遮阳与彰显威仪，尚非平民雨具。"
    },
    sources: [
      "《人民日报·海外版》2026-07-03《开合之间，「张」显中国智慧》（睡虎地秦简《日书》「伞」字，李学勤考证，为目前已知最早）",
      "秦始皇帝陵博物院/陕西省文物局：秦陵一号铜车马伞盖（高114厘米、直径122厘米、22根盖弓、可拆卸）",
      "湖南博物院藏战国《人物御龙帛画》（1973年长沙子弹库一号楚墓出土，首批禁止出国（境）展览文物）",
      "《考工记·轮人》《孔子家语·七十二弟子解》《古今注》（传世文献原文）"
    ],
    modelBasis: "形态以秦陵一号铜车马伞盖考古数据为准：圆形微拱盖面、22根盖弓、修长盖杠、可拆卸结构；盖面质感模拟锦帛。",
    prototypes: [
      { src: "prototypes/pre_qin_yulong.webp", caption: "战国《人物御龙帛画》中的伞盖（湖南博物院藏）" }
    ],
    images: {
      canopy: "",      // 伞面图（用户放置）
      side: "",
      detail: ""
    },
    background: "images/bg-pre_qin.webp",   // 底图：玄褐苍黄渐变+人物御龙帛画剪影（make_preqin_ming_bg.py）
    painting: "images/painting-pre_qin.webp",
    noModel: true,   // 华盖不做3D模型，详情页以画作为主视觉
    modelParams: {
      ribCount: 22,
      canopyRadius: 1.4,
      canopyHeight: 0.3,
      handleLength: 2.0,
      handleCurve: 0,
      openAngle: 0.15
    }
  },
  {
    id: "han",
    era: "汉代",
    period: "公元前206年 — 公元220年",
    name: "车盖",
    nameEn: "Chariot Canopy",
    description: "汉代伞具以车盖为盛：武威雷台汉墓铜轺车，伞盖插于车舆中央。",
    materials: ["丝帛盖衣", "木竹盖弓"],
    significance: "",
    museumItem: "滇国持伞铜俑（西汉，晋宁石寨山、江川李家山出土，分藏云南省博物馆、云南李家山青铜器博物馆等）",
    tabs: {
      overview: "汉代最可靠的伞具实物出自车骑仪仗：武威雷台汉墓（东汉）铜车马中的轺车「伞盖插于中央」；云南晋宁石寨山、江川李家山出土西汉持伞铜俑，双手持伞跪坐，分藏云南省博物馆、云南李家山青铜器博物馆等。",
      craft: "汉代车盖承袭秦制：圆形盖衣、盖弓辐射、中央盖杠直插车舆。滇国持伞俑伞盖「形似铁锅覆置」，边沿悬挂小铃。",
      culture: "车盖属舆服制度，是等级与威仪的象征：《后汉书·舆服志》载皇帝用羽盖、皇太子皇子用青盖、二千石以上皂盖、二百石以下白布盖。汉代画像石、画像砖中的车骑出行图普遍绘有伞盖，与雷台铜车马互为印证。"
    },
    sources: [
      "人民网《来国博，看骏马跃新春》（2026-02，雷台汉墓铜轺车，甘肃省博物馆藏）",
      "中国新闻网/上海博物馆「春风骐骥」马年生肖展资料（「双辕曲衡，舆前设护挡，伞盖插于中央」）",
      "云南省博物馆、云南李家山青铜器博物馆、昆明市博物馆藏西汉持伞铜俑（晋宁石寨山、江川李家山出土）",
      "深圳博物馆「彩云之滇——古滇国青铜文化展」文物说明（持伞铜俑伞盖形制）",
      "《后汉书·舆服志》（传世文献原文）"
    ],
    modelBasis: "以雷台汉墓铜轺车伞盖形象为准：圆形伞盖、弧度较浅、中央长盖杠；盖弓结构参照秦陵铜车马伞盖（22根，汉承秦制）。",
    prototypes: [
      { src: "prototypes/han_leitai.webp", caption: "东汉铜轺车，武威雷台汉墓出土，甘肃省博物馆藏（香港文物探知馆官方展品照）" }
    ],
    images: { canopy: "", side: "", detail: "" },
    background: "images/bg-han.webp",   // 底图：玄绛漆器渐变+顶部画像砖纹理+宣纸肌理（四川博物院车马出行画像砖）
    painting: "images/painting-han.webp",   // 时间轴卡片画作横条（拓片风处理）
    noModel: true,   // 车盖不做3D模型，详情页以画作为主视觉
    modelParams: {
      ribCount: 22,
      canopyRadius: 1.25,
      canopyHeight: 0.32,
      handleLength: 1.8,
      handleCurve: 0,
      openAngle: 0.2
    }
  },
  {
    id: "wei_jin",
    era: "魏晋南北朝",
    period: "公元220年 — 公元589年",
    name: "油纸伞",
    nameEn: "Oil-Paper Umbrella",
    description: "元魏时人「以竹碎分，并油纸，造成伞」，为油纸伞最早文献记载。",
    materials: ["油纸", "丝罗", "竹骨"],
    significance: "",
    museumItem: "",
    tabs: {
      overview: "《格致镜原》引《诗人玉屑》记载：「前代士夫皆乘车而有盖，至元魏之时，魏人以竹碎分，并油纸造成伞，便于步行骑马，伞自此始。」此为油纸伞起源最早的文献线索；因其系清人转引、宋以前纸伞实物与记载俱缺，学界对其可靠性亦持保留。",
      craft: "依《玉屑》所述，元魏之伞以竹为骨、油纸为面。魏晋南北朝纸伞今无实物与图像存世，北齐徐显秀墓壁画中的青罗伞盖为该时期唯一可靠的伞类图像：「青罗」即青色丝罗（绞经轻薄的丝织品），属仪仗用罗伞盖，由侍从张举于乘马之上，并非油纸伞。纸面与丝罗伞面当时并存，本模型形态即以该壁画伞盖为参照。",
      culture: "自后魏（北魏）起，伞正式用于官仪，成为皇家礼仪的一部分。伞自车驾附属物逐步转变为可手持的独立器具。"
    },
    sources: [
      "《格致镜原·朝制类·伞》引《诗人玉屑》（清·陈元龙）",
      "太原北齐壁画博物馆：徐显秀墓壁画（新华社/太原日报2023-12报道「青罗伞盖下一匹枣红骏马」）",
      "北齐徐显秀墓墓室西壁《鞍马仪仗图》壁画影像（Wikimedia Commons，公有领域）",
      "《浙江工艺美术》工美大观专栏·陈娜《中西伞的起源、类型及功能价值》（魏晋南北朝涂桐油纸成为伞面材料，标志油纸伞诞生）",
      "中国伞博物馆基本陈列说明（自后魏起伞正式用于官仪）"
    ],
    modelBasis: "形态参考徐显秀墓壁画青罗伞盖图像：伞面弧度较汉代加深、趋于半球形，长柄可手持；伞骨数目按传统规制设置。",
    prototypes: [
      { src: "prototypes/weijin_xuxianxiu.webp", caption: "北齐徐显秀墓壁画《墓主人出行图》青罗伞盖（太原北齐壁画博物馆）" }
    ],
    images: { canopy: "", side: "", detail: "" },
    noModel: true,   // 油纸伞（魏晋）不做3D模型，详情页以画作为主视觉
    background: "images/bg-wei_jin.webp",   // 底图：徐显秀墓壁画米色系渐变+顶部两侧壁画淡白剪影+宣纸肌理
    painting: "images/painting-wei_jin.webp",   // 时间轴卡片画作横条（徐显秀墓西壁《鞍马仪仗图》青罗伞盖与枣红骏马）
    modelParams: {
      ribCount: 16,
      canopyRadius: 1.0,
      canopyHeight: 0.35,
      handleLength: 1.4,
      handleCurve: 0.1,
      openAngle: 0.4
    }
  },
  {
    id: "tang",
    era: "唐代",
    period: "公元618年 — 公元907年",
    name: "唐伞",
    nameEn: "Tang Umbrella",
    description: "唐代中国伞传到朝鲜与日本，日本早期油纸伞被称为「唐伞」。中唐以后，绸伞进一步流行。",
    materials: ["油纸", "丝绸", "竹骨"],
    significance: "伞文化东传，「唐伞」之名远播",
    museumItem: "中国伞博物馆基本陈列「走向世界的伞」单元",
    tabs: {
      overview: "中国伞博物馆展陈指出，在唐代中国伞便传到了朝鲜与日本；日本早期油纸伞因此被称为「唐伞」。",
      craft: "唐代制伞的基本形态为竹骨、纸或丝绸为面、涂油防水；丝绸很早即用作伞面，中唐以后随丝绸普及，绸伞进一步流行。",
      culture: "「唐伞」一词是唐代文化影响力的实物见证。油纸伞传入日本、朝鲜后与当地文化结合，形成各自的伞文化传统。"
    },
    sources: [
      "人民日报社《人民周刊》2020-01-21《闲话油纸伞》（唐伞东传日韩）",
      "中国伞博物馆基本陈列第五单元「走向世界的伞」及「美丽的西湖绸伞」展陈文字（中唐以后绸伞进一步流行）"
    ],
    modelBasis: "唐代伞无实物与可靠图像存世；模型为依据文献的推测性复原，形制参考宋画中的油纸伞形象。",
    prototypes: [],
    hidden: true,
    images: { canopy: "", side: "", detail: "" },
    background: "",
    modelParams: {
      ribCount: 24,
      canopyRadius: 1.1,
      canopyHeight: 0.4,
      handleLength: 1.5,
      handleCurve: 0.15,
      openAngle: 0.4
    }
  },
  {
    id: "song",
    era: "宋代",
    period: "公元960年 — 公元1279年",
    name: "绿油纸伞",
    nameEn: "Green Oil-Paper Umbrella",
    description: "「纸伞」一词始见于北宋文献，《清明上河图》中绘有多把，时称绿油伞。",
    materials: ["绿油纸", "竹骨"],
    significance: "",
    museumItem: "",
    tabs: {
      overview: "北宋孔平仲诗「强登曹亭要远望，纸伞掣手不可操」，为目前所见「纸伞」二字最早的文献记录。南宋杨万里亦有「初初雨影伞先知」之句。",
      craft: "宋代纸伞以竹为骨、纸为面，纸面经油浸涂以防水。《清明上河图》中绘有多把或张或收的纸伞，其形制已与现代油纸伞相近。",
      culture: "宋代制度规定天子用红黄二色伞、臣庶通用青伞，实际执行屡被冲破。绿色油纸伞成为市井日常，《清明上河图》中行人、店铺用伞即是明证。"
    },
    sources: [
      "中国伞博物馆基本陈列（孔平仲诗为「纸伞」最早记录；宋代青色伞制度）",
      "故宫博物院藏北宋张择端《清明上河图》（图中绘有多把纸伞）",
      "《宋史·舆服志》（臣庶青绢伞制度）",
      "人民日报社《人民周刊》2020-01-21《闲话油纸伞》（杨万里《晚归遇雨》）"
    ],
    modelBasis: "以《清明上河图》及南宋刘松年《斗茶图》（中国伞博物馆展陈采用）中的纸伞形象为准：伞面弧度加深、竹骨加密、伞柄缩短适于手持，绿色伞面。",
    prototypes: [
      { src: "prototypes/song_qingming.webp", caption: "北宋张择端《清明上河图》中的纸伞（故宫博物院藏）" }
    ],
    images: { canopy: "", side: "", detail: "" },
    background: "images/bg-song.webp",   // 底图：青绿黛渐变+顶部山水+宣纸肌理（弗利尔《西湖清趣图》CC0）
    painting: "images/painting-song.webp",   // 时间轴卡片底部画作横条
    modelFile: "models/song/绿油纸伞.glb",
    modelEffect: "glaze",
    modelView: { scale: 1.5, offsetX: 0, offsetY: -1 },   // 23:07 修正：offsetY 正值实为画面上移，居中应为微负值
    modelParams: {
      ribCount: 24,
      canopyRadius: 1.0,
      canopyHeight: 0.34,
      handleLength: 1.55,
      handleCurve: 0,
      openAngle: 0.42,
      liningColor: 0xa03d24
    }
  },
  {
    id: "yuan",
    era: "元代",
    period: "公元1271年 — 公元1368年",
    name: "油布伞",
    nameEn: "Oilcloth Umbrella",
    description: "元代棉纺织业大发展，为布面伞奠定材料基础；棉布涂桐油防水，比油纸伞更坚固耐用。",
    materials: ["棉布（涂桐油）", "竹骨"],
    significance: "棉纺兴起，布面伞之基",
    museumItem: "中国伞博物馆基本陈列「油纸伞与油布伞」单元",
    tabs: {
      overview: "中国伞博物馆展陈指出，棉纺业兴起后，以棉布为伞面材料的油布伞出现，比油纸伞更加坚固耐用。元代黄道婆改进棉纺技术（《南村辍耕录》有载），棉纺织业大发展，为布面伞奠定了材料基础。",
      craft: "油布伞以棉布替代纸张作伞面，涂刷桐油防水。棉布较纸更坚韧耐磨；伞骨仍以竹材为主，构造与油纸伞一致。",
      culture: "油纸伞、油布伞并行的格局自明清形成后延续数百年，直至20世纪金属骨架伞普及。"
    },
    sources: [
      "中国伞博物馆基本陈列第二单元「油纸伞与油布伞」",
      "《人民日报·海外版》2026-07-03《开合之间，「张」显中国智慧》（明清时期棉纺业兴起后油布伞出现，较油纸伞更坚固耐用）",
      "元·陶宗仪《南村辍耕录》（黄道婆改进棉纺技术）"
    ],
    modelBasis: "油布伞无元代实物存世；模型形制依据与油纸伞同构、仅伞面材质不同的原则，外观参考明清油纸伞。",
    prototypes: [],
    images: { canopy: "", side: "", detail: "" },
    background: "images/bg-yuan.webp",   // 底图：柘黄渐变（呼应桐油布面）+顶部两侧赵孟頫山水白色剪影+宣纸肌理
    painting: "images/painting-yuan.webp",   // 时间轴卡片画作横条（赵孟頫青绿山水水阁苍松段）
    modelFile: "models/yuan/油布伞.glb",
    exposure: 0.75,
    viewElev: 1.8,
    modelView: { scale: 1.5, offsetX: 0, offsetY: 7.5 },   // 23:07 修正：offsetY 正值实为画面上移，居中应为微负值
    noModel: IS_XHS,   // 小红书版：3D 改静态渲染图（包体限制）
    modelImg: IS_XHS ? "images/render-yuan.webp" : "",
    modelParams: {
      ribCount: 20,
      canopyRadius: 1.05,
      canopyHeight: 0.36,
      handleLength: 1.3,
      handleCurve: 0.15,
      openAngle: 0.4
    }
  },
  {
    id: "ming",
    era: "明代",
    period: "公元1368年 — 公元1644年",
    name: "货郎伞",
    nameEn: "Peddler's Umbrella",
    description: "《货郎图》中货郎身后为撑伞盖的货架；《天工开物》载糊雨伞用小皮纸。",
    materials: ["小皮纸", "竹骨", "桐油"],
    significance: "市井商业繁荣的图像见证",
    museumItem: "",
    tabs: {
      overview: "《货郎图》自南宋李嵩以来流行，至明代大行其道。国博藏明人《货郎图》轴中，货郎「身后为一支撑伞盖的货架」；故宫藏计盛《货郎图》轴为「宫廷货郎」代表。",
      craft: "宋应星《天工开物》载「凡糊雨伞与油扇，皆用小皮纸」，即明代以小皮纸糊制伞面。货郎伞盖撑于货架之上，宽大以庇护百货。",
      culture: "《明史·舆服志》载，洪武元年令「庶民不得用罗绢凉伞，但许用油纸雨伞」。货郎伞所依托的《货郎图》传统，反映了明代市镇商业的繁荣。"
    },
    sources: [
      "中国国家博物馆藏明人《货郎图》轴（「身后为一支撑伞盖的货架」，人民政协网2023年国博新春展报道）",
      "故宫博物院官网藏品：计盛《货郎图》轴（故00005288）",
      "黄小峰《乐事还同万众心——〈货郎图〉解读》（《故宫博物院院刊》2007年第2期）",
      "《天工开物·杀青第十三·造皮纸》（明·宋应星）",
      "《明史》卷六十五·舆服一（洪武元年庶民伞制）"
    ],
    modelBasis: "以中国国家博物馆藏明人《货郎图》轴中「支撑伞盖的货架」形象为准：伞面宽大、弧度适中、长柄撑立于货架上方。",
    prototypes: [
      { src: "prototypes/ming_guobo.webp", caption: "明人《货郎图》轴——支撑伞盖的货架（中国国家博物馆藏）" },
      { src: "prototypes/ming_jisheng.webp", caption: "计盛《货郎图》轴（故宫博物院藏）" }
    ],
    images: { canopy: "", side: "", detail: "" },
    background: "images/bg-ming.webp",
    painting: "images/painting-ming.webp",
    modelFile: "models/ming/货郎伞.glb",
    exposure: 0.6,
    modelView: { scale: 2.2, offsetX: 0, offsetY: -14 },   // 23:28 用户指定：再下移确保伞盖完整（负值=下移；2.2倍下伞柄必然超出下缘；曝光0.6）
    noModel: IS_XHS,   // 小红书版：3D 改静态渲染图（包体限制）
    modelImg: IS_XHS ? "images/render-ming.webp" : "",
    modelParams: {
      ribCount: 24,
      canopyRadius: 1.0,
      canopyHeight: 0.4,
      handleLength: 1.4,
      handleCurve: 0.2,
      openAngle: 0.42
    }
  },
  {
    id: "qing",
    era: "清代",
    period: "公元1644年 — 公元1912年",
    name: "余杭油纸伞",
    nameEn: "Yuhang Oil-Paper Umbrella",
    description: "清代油纸伞制作技艺日趋成熟，产地分化，余杭为著名产地；「万民伞」为赠别清官的礼俗。",
    materials: ["皮纸", "竹骨", "桐油"],
    significance: "产地分化，万民伞礼俗",
    museumItem: "",
    tabs: {
      overview: "清代油纸伞产地分化，余杭、婺源甲路、福州等地齐名。余杭纸伞可追溯至清乾隆三十四年（1769）董文远九房伞店；福州油纸伞与脱胎漆器、角梳并称「福州三宝」，清末民初福州伞店达三百余家。",
      craft: "余杭油纸伞以竹为骨、皮纸为面，涂刷桐油，工序繁复。中国伞博物馆藏「余杭江南春色油纸伞」，伞面绘江南春景。",
      culture: "清代形成「万民伞」礼俗：官员离任时，绅民集资制伞、签名相赠。中国伞博物馆藏清光绪十四年（1888）丝织绣万民伞即其实物。"
    },
    sources: [
      "人民日报社《人民周刊》2020-01-21《闲话油纸伞》（福州三宝、清末民初福州伞店三百余家）",
      "中国伞博物馆藏品：余杭江南春色油纸伞（《人民日报·海外版》2026-07-03报道配图）",
      "人民网文化频道2014-05-14：清光绪十四年丝织绣万民伞（中国伞博物馆藏）",
      "中国伞博物馆展陈（余杭清乾隆三十四年董文远九房伞店）"
    ],
    modelBasis: "以余杭油纸伞实物为准：标准油纸伞形态，伞面饱满、弧度适中，伞面绘江南春景；伞骨数目按传统规制设置。",
    prototypes: [
      { src: "prototypes/qing_yuhang.webp", caption: "余杭江南春色油纸伞，伞面手绘江南春景并书杜牧《江南春》诗（中国伞博物馆藏）" }
    ],
    images: { canopy: "", side: "", detail: "" },
    background: "images/bg-qing.webp",   // 底图：柳青渐变+顶部两侧陈枚山水楼阁白色剪影+宣纸肌理
    painting: "images/painting-qing.webp",   // 时间轴卡片画作横条（陈枚《山水楼阁图册》楼阁主体段）
    modelFile: "models/qing/余杭油纸伞.glb",
    exposure: 0.9,
    viewElev: 2.2,
    modelView: { scale: 1.5, offsetX: 0, offsetY: 1, scaleWide: 1.35, offsetYWide: -14 },   // 2026-07-23 用户指定：模型再放大、位置下移；桌面布局单独收敛防压伞名横幅
    modelParams: {
      ribCount: 32,
      canopyRadius: 1.1,
      canopyHeight: 0.42,
      handleLength: 1.4,
      handleCurve: 0.15,
      openAngle: 0.45
    }
  },
  {
    id: "xihu_silk",
    era: "民国",
    period: "公元1932年",
    name: "西湖绸伞",
    nameEn: "West Lake Silk Umbrella",
    description: "1932年杭州都锦生丝织厂研创，淡竹为骨、丝绸为面，后列入国家级非遗。",
    materials: ["杭州丝绸", "杭州淡竹", "花线"],
    significance: "国家级非物质文化遗产",
    museumItem: "西湖绸伞（中国伞博物馆藏）",
    tabs: {
      overview: "西湖绸伞1932年创制于杭州都锦生丝织厂，被誉为「西湖之花」。2008年，伞制作技艺（西湖绸伞）列入第二批国家级非遗名录（编号Ⅷ-140，与泸州油纸伞制作技艺同属「伞制作技艺」项目）。",
      craft: "以杭州本地淡竹（传统取材于富阳等地）为骨、杭绸为面，共18道工序。伞骨采用套合技艺、不用黏合剂；伞面以刷花、绘花、绣花「三花」装饰，收拢不见伞面。",
      culture: "许仙与白娘子断桥借伞的传说，使伞与西湖结下情缘。西湖绸伞将这一意象凝为实物，成为杭州城市文化的名片。"
    },
    sources: [
      "中国非物质文化遗产网：伞制作技艺（西湖绸伞），编号Ⅷ-140，2008年第二批国家级非遗名录（与泸州油纸伞制作技艺同属）",
      "潮新闻《近百年前的西湖绸伞长啥样？》（2025-12：都锦生1932年创制、富阳淡竹、传承人宋志明）",
      "人民网浙江《「再续前缘」——都锦生与西湖绸伞创新特展启幕》（2025-09）",
      "画作：丰子恺《春雨》封面画（1930年开明书店版，公有领域；图据汪家明《美术给予我的》）"
    ],
    modelBasis: "以馆藏1930年代首创期西湖绸伞实物为参照：伞径约80厘米、丝绸伞面夹藏于伞骨之间、收拢呈竹节状。",
    prototypes: [
      { src: "prototypes/xihu_silk.webp", caption: "双面绣蝶恋花西湖绸伞（杭州工艺美术博物馆藏）" }
    ],
    images: { canopy: "", side: "", detail: "" },
    background: "images/bg-xihu_silk.webp",   // 底图：苔绿-湖绿渐变+顶部春雨伞骨纹理+宣纸肌理（丰子恺《春雨》1930）
    painting: "",   // 详情页不展示画作图
    modelFile: "models/minguo/西湖绸伞.glb",
    exposure: 0.7,
    modelView: { scale: 1.7, offsetX: 0, offsetY: -4, scaleWide: 1.45, offsetYWide: -10 },   // 2026-07-23 用户指定：模型放大、位置下移（负值=下移）；桌面布局单独收敛防压伞名横幅
    modelParams: {
      ribCount: 32,
      canopyRadius: 1.0,
      canopyHeight: 0.35,
      handleLength: 1.3,
      handleCurve: 0.25,
      openAngle: 0.4
    }
  },
  {
    id: "guohuo",
    era: "民国",
    period: "公元20世纪30年代",
    name: "八角油纸伞",
    nameEn: "Octagonal Oil-Paper Umbrella",
    description: "中国伞博物馆镇馆之宝，32根伞骨撑起八角伞面，手书「请用国货」。",
    materials: ["油纸", "竹骨（32根）", "桐油"],
    significance: "镇馆之宝，国货运动实物见证",
    museumItem: "",
    tabs: {
      overview: "此伞制作于20世纪30年代，长62厘米，由32根伞骨撑起，为中国伞博物馆镇馆之宝。其背景是1915年兴起、九一八后达到顶峰的国货运动。",
      craft: "伞面呈八角形，以黑、绿为主色调；中部彩绘梅、兰、竹、菊，图案之间手书「请用国货」四字；八个角均为黑底白花装饰。",
      culture: "「请用国货」是国人抵制外货、振兴民族实业的呐喊。此伞见证了「实业救国」思潮从理念到实践的轨迹。"
    },
    sources: [
      "杭州网·每日商报《中国伞博物馆：民国「请用国货」八角油纸伞》（2025-10-07：20世纪30年代制、长62厘米、32根伞骨）",
      "杭州工艺美术博物馆群官方社教资料（「镇馆之宝」定位）",
      "拱墅区官方报道（2026-05-18：墨笔手书「请用国货」四字）",
      "画作：赵藕生月份牌（1919年，上海市历史博物馆藏，公有领域；五四时期女学生街头宣传图，图据澎湃新闻报道）"
    ],
    modelBasis: "以馆藏文物实测为准：伞面呈正八边形（非圆形）、32根伞骨、黑绿主色调、八角黑底白花装饰、中部手书「请用国货」。",
    prototypes: [
      { src: "prototypes/guohuo.webp", caption: "民国「请用国货」八角形油纸伞（中国伞博物馆藏）" }
    ],
    images: { canopy: "textures/bajiao_canopy.png", side: "", detail: "" },
    background: "images/bg-minguo.webp",   // 底图：沉香茶色渐变+顶部赵藕生月份牌街景纹理+宣纸肌理
    painting: "",   // 详情页不展示画作
    modelFile: "models/minguo/八角油纸伞.glb",
    exposure: 0.9,
    viewElev: 2.2,
    modelView: { scale: 1.5, offsetX: -1.3, offsetY: -4 },   // 23:22 用户指定：位置下移（负值=下移）
    noModel: IS_XHS,   // 小红书版：3D 改静态渲染图（包体限制）
    modelImg: IS_XHS ? "images/render-guohuo.webp" : "",
    modelParams: {
      ribCount: 32,
      canopyRadius: 1.05,
      canopyHeight: 0.4,
      handleLength: 1.35,
      handleCurve: 0,
      openAngle: 0.45
    }
  }
];

/**
 * 在时间轴展示的条目（hidden 条目不展示）
 */
export const visibleUmbrellas = umbrellaData.filter(u => !u.hidden);

/**
 * 按ID获取伞数据
 */
export function getUmbrellaById(id) {
  return umbrellaData.find(u => u.id === id) || null;
}

/**
 * 获取展示条目索引
 */
function getVisibleIndex(id) {
  return visibleUmbrellas.findIndex(u => u.id === id);
}

/**
 * 获取上一把伞（在展示条目内）
 */
export function getPrevUmbrella(id) {
  const idx = getVisibleIndex(id);
  if (idx > 0) return visibleUmbrellas[idx - 1];
  return null;
}

/**
 * 获取下一把伞（在展示条目内）
 */
export function getNextUmbrella(id) {
  const idx = getVisibleIndex(id);
  if (idx >= 0 && idx < visibleUmbrellas.length - 1) return visibleUmbrellas[idx + 1];
  return null;
}
